"use client";

import Link from "next/link";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PurchaseOrderFields } from "@/components/orders/purchase-order-fields";
import { formatCents } from "@/lib/formatting/currency";
import { TERMS_ON_HOLD_COPY, applyForTermsHref, companySetupHref } from "@/lib/credit/terms-links";
import type { CheckoutTermsDecision, TermsPayment } from "@/hooks/checkout/use-terms-payment";

function QuietNote({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-sm text-muted-foreground">{children}</p>;
}

function termsUnavailableReason(
  decision: CheckoutTermsDecision | undefined,
  returnTo: string,
): React.ReactNode {
  if (decision === undefined) return "Checking your available credit…";
  if (decision.kind === "over_limit") {
    return (
      <>
        This order is more than your available credit ({formatCents(decision.availableCents)}).
        Pay by card
        {decision.canApplyForMore ? (
          <>
            , or{" "}
            <Link href={applyForTermsHref(returnTo)} className="text-link">
              apply for a higher limit
            </Link>
          </>
        ) : (
          <>. Your request to raise your limit is under review</>
        )}
        .
      </>
    );
  }
  if (decision.kind === "exposure_incomplete") {
    return "We couldn't total your open invoices just now. Try again in a few minutes, or pay by card.";
  }
  return null;
}

function TermsChoice({ payment, returnTo }: { payment: TermsPayment; returnTo: string }) {
  const reason = payment.onTerms ? termsUnavailableReason(payment.decision, returnTo) : null;
  return (
    <fieldset className="mt-4 space-y-2">
      <legend className="text-sm font-medium text-foreground">Payment</legend>
      <RadioGroup
        value={payment.choice}
        onValueChange={(value) => payment.setChoice(value === "terms" ? "terms" : "card")}
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="card" id="payment-choice-card" />
          <Label htmlFor="payment-choice-card">Card</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="terms" id="payment-choice-terms" />
          <Label htmlFor="payment-choice-terms">
            Pay on terms{payment.termsLabel ? ` (${payment.termsLabel})` : ""}
          </Label>
        </div>
      </RadioGroup>
      {reason ? (
        <p role="status" className="text-sm text-muted-foreground">
          {reason}
        </p>
      ) : null}
      {payment.onTerms ? (
        <div className="pt-1">
          <PurchaseOrderFields
            idPrefix="terms"
            value={payment.purchaseOrder}
            onChange={payment.setPurchaseOrder}
          />
        </div>
      ) : null}
    </fieldset>
  );
}

const APPROVED_KINDS: ReadonlySet<CheckoutTermsDecision["kind"]> = new Set([
  "terms",
  "over_limit",
  "exposure_incomplete",
]);

export function PaymentChoice({ payment, returnTo }: { payment: TermsPayment; returnTo: string }) {
  const { decision } = payment;
  if (decision?.kind === "apply" && decision.previous) {
    return (
      <QuietNote>
        {decision.previous === "declined"
          ? "Your company's payment terms application was declined."
          : "Your company's payment terms application expired."}{" "}
        <Link href={applyForTermsHref(returnTo)} className="text-link">
          Apply again
        </Link>{" "}
        to pay on terms.
      </QuietNote>
    );
  }
  if (payment.isGuest || decision?.kind === "apply") {
    return (
      <QuietNote>
        Buying for a business?{" "}
        <Link href={applyForTermsHref(returnTo)} className="text-link">
          Apply for terms
        </Link>
      </QuietNote>
    );
  }
  if (decision?.kind === "needs_company") {
    return (
      <QuietNote>
        Buying for a business?{" "}
        <Link href={companySetupHref(returnTo)} className="text-link">
          Set up your company
        </Link>{" "}
        to apply for terms.
      </QuietNote>
    );
  }
  if (decision?.kind === "pending") return <QuietNote>Your terms application is under review.</QuietNote>;
  if (decision?.kind === "on_hold") return <QuietNote>{TERMS_ON_HOLD_COPY}</QuietNote>;
  if (decision?.kind === "billing_contact_only") {
    return <QuietNote>An owner, admin or accounting member at your company can pay on terms.</QuietNote>;
  }
  if ((decision && APPROVED_KINDS.has(decision.kind)) || (payment.onTerms && decision === undefined)) {
    return <TermsChoice payment={payment} returnTo={returnTo} />;
  }
  return null;
}
